// Scans the user's Claude Code "arsenal" — installed skills, subagents, and MCP
// servers — across project, personal (~/.claude), and plugin scopes, so the
// dashboard can show what's available without dropping to the CLI.
//
// This reads Claude Code's own on-disk layout (NOT Synthra's graph):
//   skills   ~/.claude/skills/<name>/SKILL.md, <project>/.claude/skills/…, plugin skills/
//   agents   ~/.claude/agents/*.md, <project>/.claude/agents/*.md, plugin agents/
//   mcp      <project>/.mcp.json, ~/.claude.json mcpServers, plugin .mcp.json
//   plugins  ~/.claude/plugins/installed_plugins.json (index) + settings.json enabledPlugins
//
// Security: MCP configs frequently carry auth headers/env tokens. We emit ONLY
// name / type / url(query stripped) / command — headers, env, and args are
// dropped so nothing secret reaches the dashboard.

import { readFile, readdir } from "node:fs/promises";
import { homedir } from "node:os";
import { basename, dirname, join } from "node:path";

export type ArsenalScope = "project" | "personal" | "plugin";

export interface ArsenalItem {
  name: string;
  description: string;
  scope: ArsenalScope;
  /** Plugin name when scope === "plugin". */
  source?: string;
  /** Plugin items: enabled state from settings.json; undefined = always-on (own files). */
  enabled?: boolean;
  /** Kind-specific extras: agents {tools,model}; skills {argument_hint,user_invocable}; mcp {type,url}. */
  meta?: Record<string, string>;
}

export interface ArsenalData {
  skills: ArsenalItem[];
  agents: ArsenalItem[];
  mcp: ArsenalItem[];
  counts: { skills: number; agents: number; mcp: number; plugins: number };
  scanned_at: string;
}

const DESC_MAX = 300;
const TOOLS_MAX = 200;

async function readText(path: string): Promise<string | null> {
  try {
    return await readFile(path, "utf8");
  } catch {
    return null;
  }
}

async function readJson<T>(path: string): Promise<T | null> {
  const text = await readText(path);
  if (text === null) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

async function listNames(dir: string): Promise<string[]> {
  try {
    return await readdir(dir);
  } catch {
    return [];
  }
}

function clip(s: string, max: number): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/**
 * Minimal YAML-frontmatter reader — no dependency (Synthra stays lean). Pulls
 * the column-0 `key: value` pairs from the leading `--- … ---` block. Supports
 * single-line values and quoted values that span multiple lines (agent
 * `description` blocks do). Nested/indented lines (e.g. under `metadata:`) are
 * ignored. Keys are returned verbatim (e.g. "argument-hint").
 */
export function parseFrontmatter(md: string): Record<string, string> {
  const m = md.match(/^﻿?\s*---\r?\n([\s\S]*?)\r?\n---/);
  if (!m) return {};
  const lines = (m[1] ?? "").split(/\r?\n/);
  const out: Record<string, string> = {};
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const kv = line.match(/^([A-Za-z][\w-]*):\s?(.*)$/); // column-0 keys only
    if (!kv) continue;
    const key = kv[1] ?? "";
    if (!key) continue;
    let val = kv[2] ?? "";
    // Quoted value that doesn't close on this line → consume until the closer.
    if ((val.startsWith('"') && !/[^\\]"\s*$/.test(val.slice(1))) || val === '"') {
      const buf = [val];
      while (i + 1 < lines.length && !/"\s*$/.test(buf[buf.length - 1] ?? "")) {
        i += 1;
        buf.push(lines[i] ?? "");
      }
      val = buf.join(" ");
    }
    val = val
      .trim()
      .replace(/^["']|["']$/g, "")
      .trim();
    out[key] = val;
  }
  return out;
}

function skillItem(
  fm: Record<string, string>,
  fallbackName: string,
  scope: ArsenalScope,
  source?: string,
): ArsenalItem {
  const meta: Record<string, string> = {};
  if (fm["argument-hint"]) meta.argument_hint = fm["argument-hint"];
  if (fm["user-invocable"]) meta.user_invocable = fm["user-invocable"];
  return {
    name: fm.name || fallbackName,
    description: clip(fm.description || "", DESC_MAX),
    scope,
    ...(source ? { source } : {}),
    ...(Object.keys(meta).length ? { meta } : {}),
  };
}

function agentItem(
  fm: Record<string, string>,
  fallbackName: string,
  scope: ArsenalScope,
  source?: string,
): ArsenalItem {
  const meta: Record<string, string> = {};
  if (fm.tools) meta.tools = clip(fm.tools, TOOLS_MAX);
  if (fm.model) meta.model = fm.model;
  return {
    name: fm.name || fallbackName,
    description: clip(fm.description || "", DESC_MAX),
    scope,
    ...(source ? { source } : {}),
    ...(Object.keys(meta).length ? { meta } : {}),
  };
}

async function scanSkillsDir(
  dir: string,
  scope: ArsenalScope,
  source: string | undefined,
  out: ArsenalItem[],
): Promise<void> {
  for (const name of await listNames(dir)) {
    const md = await readText(join(dir, name, "SKILL.md"));
    if (md === null) continue; // not a skill dir (or broken symlink) — skip
    out.push(skillItem(parseFrontmatter(md), name, scope, source));
  }
}

async function scanAgentsDir(
  dir: string,
  scope: ArsenalScope,
  source: string | undefined,
  out: ArsenalItem[],
): Promise<void> {
  for (const file of await listNames(dir)) {
    if (!file.endsWith(".md")) continue;
    const md = await readText(join(dir, file));
    if (md === null) continue;
    out.push(agentItem(parseFrontmatter(md), basename(file, ".md"), scope, source));
  }
}

/** Redacted MCP server entries — name/type/url only, never headers/env/args. */
function mcpItemsFrom(
  json: unknown,
  scope: ArsenalScope,
  source: string | undefined,
): ArsenalItem[] {
  if (!json || typeof json !== "object") return [];
  const record = json as Record<string, unknown>;
  // Either { mcpServers: {...} } or a bare { name: {type,url} } map.
  const servers =
    record.mcpServers && typeof record.mcpServers === "object"
      ? (record.mcpServers as Record<string, unknown>)
      : record;
  const items: ArsenalItem[] = [];
  for (const [name, raw] of Object.entries(servers)) {
    if (!raw || typeof raw !== "object") continue;
    const cfg = raw as Record<string, unknown>;
    const type = typeof cfg.type === "string" ? cfg.type : cfg.command ? "stdio" : "http";
    const url =
      typeof cfg.url === "string"
        ? cfg.url.split("?")[0]
        : typeof cfg.command === "string"
          ? cfg.command
          : "";
    const meta: Record<string, string> = { type };
    if (url) meta.url = url;
    items.push({ name, description: "", scope, ...(source ? { source } : {}), meta });
  }
  return items;
}

interface InstalledEntry {
  scope?: string;
  installPath?: string;
  version?: string;
}

const SCOPE_ORDER: Record<ArsenalScope, number> = { project: 0, personal: 1, plugin: 2 };

function sortItems(items: ArsenalItem[]): ArsenalItem[] {
  return items.sort((a, b) => {
    if (a.scope !== b.scope) return SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope];
    const sa = a.source ?? "";
    const sb = b.source ?? "";
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });
}

let cache: { key: string; at: number; data: ArsenalData } | null = null;
const CACHE_TTL_MS = 15_000;

export async function computeArsenal(
  projectRoot: string,
  homeDir = homedir(),
): Promise<ArsenalData> {
  const key = `${projectRoot} ${homeDir}`;
  const now = Date.now();
  if (cache && cache.key === key && now - cache.at < CACHE_TTL_MS) return cache.data;

  const homeClaude = join(homeDir, ".claude");
  const projClaude = join(projectRoot, ".claude");

  const skills: ArsenalItem[] = [];
  const agents: ArsenalItem[] = [];
  const mcp: ArsenalItem[] = [];

  // --- own files: project, then personal ---
  await scanSkillsDir(join(projClaude, "skills"), "project", undefined, skills);
  await scanSkillsDir(join(homeClaude, "skills"), "personal", undefined, skills);
  await scanAgentsDir(join(projClaude, "agents"), "project", undefined, agents);
  await scanAgentsDir(join(homeClaude, "agents"), "personal", undefined, agents);
  mcp.push(...mcpItemsFrom(await readJson(join(projectRoot, ".mcp.json")), "project", undefined));
  mcp.push(
    ...mcpItemsFrom(
      (await readJson<Record<string, unknown>>(join(homeDir, ".claude.json")))?.mcpServers,
      "personal",
      undefined,
    ),
  );

  // --- plugins: installed_plugins.json gives exact installPath per plugin ---
  // installed_plugins.json is `{ version, plugins: { "<key>": [entries] } }`
  // (v2); tolerate an older flat `{ "<key>": [entries] }` shape too.
  const installedRaw = await readJson<Record<string, unknown>>(
    join(homeClaude, "plugins", "installed_plugins.json"),
  );
  const pluginsMap = (installedRaw?.plugins ?? installedRaw ?? {}) as Record<
    string,
    InstalledEntry[]
  >;
  const settings = await readJson<{ enabledPlugins?: Record<string, boolean> }>(
    join(homeClaude, "settings.json"),
  );
  const enabledMap = settings?.enabledPlugins ?? {};
  let pluginCount = 0;
  for (const [pluginKey, entries] of Object.entries(pluginsMap)) {
    const entry = Array.isArray(entries) ? entries[0] : undefined;
    if (!entry?.installPath) continue;
    pluginCount += 1;
    const pluginName = pluginKey.split("@")[0];
    const enabled = enabledMap[pluginKey] !== false;
    const root = entry.installPath;

    // Layouts vary: some plugins keep agents/skills in agents/ + skills/
    // subdirs (feature-dev), others at the plugin root listed in plugin.json
    // (voltagent). Take the UNION of both, deduped by file path.
    const manifest = await readJson<{ agents?: string[]; skills?: string[] }>(
      join(root, ".claude-plugin", "plugin.json"),
    );

    const agentFiles = new Set<string>();
    for (const f of await listNames(join(root, "agents"))) {
      if (f.endsWith(".md")) agentFiles.add(join(root, "agents", f));
    }
    for (const rel of manifest?.agents ?? []) agentFiles.add(join(root, rel));
    const pAgents: ArsenalItem[] = [];
    for (const file of agentFiles) {
      const md = await readText(file);
      if (md !== null)
        pAgents.push(agentItem(parseFrontmatter(md), basename(file, ".md"), "plugin", pluginName));
    }

    const skillMds = new Set<string>();
    for (const name of await listNames(join(root, "skills"))) {
      skillMds.add(join(root, "skills", name, "SKILL.md"));
    }
    for (const rel of manifest?.skills ?? []) {
      skillMds.add(rel.endsWith(".md") ? join(root, rel) : join(root, rel, "SKILL.md"));
    }
    const pSkills: ArsenalItem[] = [];
    for (const md of skillMds) {
      const text = await readText(md);
      if (text !== null)
        pSkills.push(
          skillItem(parseFrontmatter(text), basename(dirname(md)), "plugin", pluginName),
        );
    }

    const pMcp = mcpItemsFrom(await readJson(join(root, ".mcp.json")), "plugin", pluginName);
    for (const it of [...pSkills, ...pAgents, ...pMcp]) it.enabled = enabled;
    skills.push(...pSkills);
    agents.push(...pAgents);
    mcp.push(...pMcp);
  }

  // dedupe skills by scope+source+name (symlink/dir overlaps)
  const seen = new Set<string>();
  const dedupedSkills = skills.filter((s) => {
    const k = `${s.scope}:${s.source ?? ""}:${s.name}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const data: ArsenalData = {
    skills: sortItems(dedupedSkills),
    agents: sortItems(agents),
    mcp: sortItems(mcp),
    counts: {
      skills: dedupedSkills.length,
      agents: agents.length,
      mcp: mcp.length,
      plugins: pluginCount,
    },
    scanned_at: new Date(now).toISOString(),
  };
  cache = { key, at: now, data };
  return data;
}
